import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FaRegWindowMinimize } from 'react-icons/fa';
import { FiClock, FiDownload, FiMoreVertical, FiSliders, FiTrash2 } from 'react-icons/fi';
import AudioPlayerSleepTimerControls from './AudioPlayerSleepTimerControls';

export default function AudioPlayerMobileToolsMenu({
  allowEqualizer = true,
  equalizerOpen = false,
  onToggleEqualizer,
  allowSleepTimer = true,
  sleepTimer,
  allowOffline = false,
  offlineKey = '',
  offlineReady = false,
  offlineBusy = false,
  canDownload = false,
  onDownloadOffline,
  onDeleteOffline,
  onWindowMinimize = null,
  onWindowClose = null,
}) {
  const menuId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState('root');

  const hasOffline = allowOffline && Boolean(offlineKey);
  const hasWindowActions = Boolean(onWindowMinimize || onWindowClose);
  const hasOfflineSection = hasOffline && (canDownload || offlineReady);
  const hasAny =
    allowEqualizer ||
    (allowSleepTimer && sleepTimer) ||
    hasOfflineSection ||
    hasWindowActions;

  const close = useCallback(() => {
    setOpen(false);
    setPanel('root');
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      close();
    };
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onDocPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDocPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!hasAny) return null;

  const eqActive = equalizerOpen || false;

  return (
    <div className="audio-player__mobile-tools" ref={rootRef}>
      <button
        type="button"
        className={`audio-player__mobile-tools-trigger${
          open || eqActive || sleepTimer?.isActive || offlineReady
            ? ' audio-player__mobile-tools-trigger--active'
            : ''
        }`}
        aria-label={open ? 'Close player options' : 'Player options'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => {
          setOpen((was) => {
            if (was) {
              setPanel('root');
              return false;
            }
            setPanel('root');
            return true;
          });
        }}
      >
        <FiMoreVertical aria-hidden />
      </button>

      {open ? (
        <div id={menuId} className="audio-player__mobile-tools-menu" role="menu">
          {panel === 'root' ? (
            <ul className="audio-player__mobile-tools-list">
              {allowEqualizer ? (
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className={`audio-player__mobile-tools-item${
                      eqActive ? ' audio-player__mobile-tools-item--active' : ''
                    }`}
                    onClick={() => {
                      onToggleEqualizer?.();
                      close();
                    }}
                  >
                    <FiSliders aria-hidden />
                    <span>{equalizerOpen ? 'Hide equalizer' : 'Equalizer'}</span>
                  </button>
                </li>
              ) : null}
              {allowSleepTimer && sleepTimer ? (
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className={`audio-player__mobile-tools-item${
                      sleepTimer.isActive ? ' audio-player__mobile-tools-item--active' : ''
                    }`}
                    onClick={() => setPanel('sleep')}
                  >
                    <FiClock aria-hidden />
                    <span>Sleep timer</span>
                    {sleepTimer.isActive && sleepTimer.remainingLabel ? (
                      <span className="audio-player__mobile-tools-item-meta">{sleepTimer.remainingLabel}</span>
                    ) : null}
                  </button>
                </li>
              ) : null}
              {hasOffline && canDownload ? (
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="audio-player__mobile-tools-item"
                    disabled={offlineBusy}
                    onClick={() => {
                      onDownloadOffline?.();
                      close();
                    }}
                  >
                    <FiDownload aria-hidden />
                    <span>{offlineReady ? 'Update offline copy' : 'Save offline'}</span>
                  </button>
                </li>
              ) : null}
              {hasOffline && offlineReady ? (
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="audio-player__mobile-tools-item audio-player__mobile-tools-item--danger"
                    disabled={offlineBusy}
                    onClick={() => {
                      onDeleteOffline?.();
                      close();
                    }}
                  >
                    <FiTrash2 aria-hidden />
                    <span>Remove offline copy</span>
                  </button>
                </li>
              ) : null}
              {hasWindowActions && (allowEqualizer || (allowSleepTimer && sleepTimer) || hasOfflineSection) ? (
                <li role="separator" className="audio-player__mobile-tools-separator" aria-hidden />
              ) : null}
              {onWindowMinimize ? (
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="audio-player__mobile-tools-item"
                    onClick={() => {
                      onWindowMinimize();
                      close();
                    }}
                  >
                    <FaRegWindowMinimize aria-hidden />
                    <span>Minimize</span>
                  </button>
                </li>
              ) : null}
              {onWindowClose ? (
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="audio-player__mobile-tools-item"
                    onClick={() => {
                      onWindowClose();
                      close();
                    }}
                  >
                    <span className="audio-player__mobile-tools-item-icon" aria-hidden>
                      ×
                    </span>
                    <span>Close player</span>
                  </button>
                </li>
              ) : null}
            </ul>
          ) : null}

          {panel === 'sleep' && allowSleepTimer && sleepTimer ? (
            <div className="audio-player__mobile-tools-panel">
              <button
                type="button"
                className="audio-player__mobile-tools-back"
                onClick={() => setPanel('root')}
              >
                ← Back
              </button>
              <AudioPlayerSleepTimerControls sleepTimer={sleepTimer} embedded />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
